import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { colors } from '@alixpartners/ui-components';
import { IQuarter, IYearOnYearChartProps } from 'utils/types';
import { CHART_COLOR_PALLETTE } from 'utils/constants';
import { getFormattedCurrency, getFormattedPercentage } from 'utils/format';
import {
  TooltipContainer,
  TooltipItem,
  TooltipItemLabel,
  TooltipItemTrend,
  TooltipItemValue,
  Wrapper,
} from './LineChart.styles';
import { renderToString } from 'react-dom/server';

const TOOLTIP_PADDING = 16;
const TOOLTIP_HEIGHT = 120;

export const LineChart = ({
  categories,
  chartRefObj,
  format,
  series,
}: IYearOnYearChartProps) => {
  const tooltipBarId = 'tooltip-bar-123-' + Math.ceil(Math.random() * 1000);
  const chartOptions = {
    chart: {
      className: 'line-chart',
      height: '352px',
    },
    title: {
      text: null,
    },
    colors: CHART_COLOR_PALLETTE,
    legend: {
      align: 'right',
      verticalAlign: 'top',
      symbolHeight: 40,
      symbolWidth: 50,
      // @ts-ignore
      labelFormatter: function () {
        // @ts-ignore
        return this.userOptions.legendLabel;
      },
      y: -15,
    },
    xAxis: {
      categories,
    },
    yAxis: {
      labels: {
        // @ts-ignore
        formatter: function () {
          return `<strong>${getFormattedCurrency({
            currencyFormat: format,
            // @ts-ignore
            value: this.value,
            isCurrencySign: true,
            isIdealDenomination: true,
            isUnitSign: true,
          })}</strong>`;
        },
      },
      useHTML: true,
      title: {
        enabled: false,
      },
    },
    credits: { enabled: false },
    exporting: { enabled: false },

    tooltip: {
      className: 'line-chart-tooltip',
      crosshairs: true,
      shared: true,
      useHTML: true,
      outside: true,
      backgroundColor: colors.white,
      borderWidth: 0,
      borderRadius: 2,
      formatter: function () {
        // @ts-ignore
        const { quarters } = this.points[0].series.userOptions;
        const quarterObj = quarters.find(
          // @ts-ignore
          (item: IQuarter) => item.quarter === this.x
        );
        const {
          currentPercentageOfRevenue,
          currentRange,
          currentSpend,
          previousPercentageOfRevenue,
          previousRange,
          previousSpend,
          quarter,
          change,
          variancePercentageOfRevenue,
        } = quarterObj;
        const currentSpendFormatted = getFormattedCurrency({
          currencyFormat: format,
          value: currentSpend,
          isIdealDenomination: true,
          isCurrencySign: true,
          isUnitSign: true,
        });
        const currentPercentageFormatted = getFormattedPercentage(
          currentPercentageOfRevenue,
          format
        );
        const previousSpendFormatted = getFormattedCurrency({
          currencyFormat: format,
          value: previousSpend,
          isIdealDenomination: true,
          isCurrencySign: true,
          isUnitSign: true,
        });
        const previousPercentageFormatted = getFormattedPercentage(
          previousPercentageOfRevenue,
          format
        );
        const icon =
          change !== 0 ? (
            <TooltipItemTrend up={change > 0}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 0 24 24"
                width="24"
              >
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill={colors.grey4} d="M22 12l-4-4v3H3v2h15v3z" />
              </svg>
            </TooltipItemTrend>
          ) : (
            ''
          );
        return renderToString(
          <TooltipContainer id={tooltipBarId}>
            <TooltipItem>
              <TooltipItemLabel>
                {quarter} {currentRange.startYear}
              </TooltipItemLabel>
              <TooltipItemValue>
                {currentSpendFormatted}{' '}
                {currentPercentageOfRevenue
                  ? `(${currentPercentageFormatted} of rev)`
                  : ''}
              </TooltipItemValue>
            </TooltipItem>
            <TooltipItem>
              <TooltipItemLabel>
                {quarter} {previousRange.startYear}
              </TooltipItemLabel>
              <TooltipItemValue>
                {previousSpendFormatted}{' '}
                {previousPercentageOfRevenue
                  ? `(${previousPercentageFormatted} of rev)`
                  : ''}
              </TooltipItemValue>
            </TooltipItem>
            <TooltipItem>
              <TooltipItemLabel>Change</TooltipItemLabel>
              <TooltipItemValue>
                {icon}{' '}
                {getFormattedCurrency({
                  value: Math.abs(change),
                  currencyFormat: format,
                  isIdealDenomination: true,
                  isCurrencySign: true,
                  isUnitSign: true,
                })}{' '}
                {variancePercentageOfRevenue
                  ? `(${getFormattedPercentage(
                      variancePercentageOfRevenue,
                      format
                    )})`
                  : ''}
              </TooltipItemValue>
            </TooltipItem>
          </TooltipContainer>
        );
      },
      // @ts-ignore
      positioner: function (_x, _y, point) {
        const tooltipBar = document.getElementById(tooltipBarId);
        if (tooltipBar && chartRef) {
          const {
            width,
            x,
            y,
            // @ts-ignore
          } = chartRef.getBoundingClientRect();
          const tooltipRealWidth = tooltipBar.clientWidth;
          const maxX = width - tooltipRealWidth - TOOLTIP_PADDING;
          // @ts-ignore
          const xOffset = (width - this.chart.plotWidth) / 2;
          const newX = Math.min(
            maxX,
            point.plotX - tooltipRealWidth / 2 + xOffset
          );
          return {
            x: Math.max(0, newX) + x,
            y: y - TOOLTIP_HEIGHT,
          };
        }
        return {
          x: point.x,
          y: point.y,
        };
      },
    },
    series: series.map((s, index) => ({
      ...s,
      color: CHART_COLOR_PALLETTE[index],
    })),
    plotOptions: {
      line: {
        states: {
          hover: {
            symbol: 'circle',
          },
        },
      },
      series: {
        marker: {
          enabled: false,
          symbol: 'circle',
          lineColor: null,
        },
        states: {
          hover: {
            marker: {
              enabled: true,
              symbol: 'circle',
              fillColor: colors.white,
              lineColor: null,
            },
          },
        },
      },
    },
  };
  const [chartRef, setChartRef] = React.useState<React.ReactNode>(null);
  React.useLayoutEffect(() => {
    if (wrapperRef && wrapperRef.current) {
      // @ts-ignore
      setChartRef(wrapperRef.current);
    }
  }, []);

  const wrapperRef = React.useRef(null);
  return (
    <Wrapper ref={wrapperRef}>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartRefObj}
      />
    </Wrapper>
  );
};

// This code meant to show tooltip arrow in shared tooltip mode
// It doesn't conflict with other charts
// https://www.highcharts.com/docs/extending-highcharts/extending-highcharts
// https://stackoverflow.com/questions/48301320/highcharts-stacked-bar-charts-tooltip-enable-shared-and-keep-display-arrow
// @ts-ignore
Highcharts.wrap(
  Highcharts.Tooltip.prototype,
  'move',
  // @ts-ignore
  function (proceed: any, x: number, y: number, anchorX: number, anchorY: any) {
    // @ts-ignore
    const tooltip = this;
    const now = tooltip.now;
    const animate =
        tooltip.options.animation !== false &&
        !tooltip.isHidden &&
        // When we get close to the target position, abort animation and
        // land on the right place (#3056)
        (Math.abs(x - now.x) > 1 || Math.abs(y - now.y) > 1),
      skipAnchor = false; //tooltip.followPointer || tooltip.len > 1;

    // Get intermediate values for animation
    Highcharts.extend(now, {
      x: animate ? (2 * now.x + x) / 3 : x,
      y: animate ? (now.y + y) / 2 : y,
      anchorX: skipAnchor
        ? undefined
        : animate
        ? (2 * now.anchorX + anchorX) / 3
        : anchorX,
      anchorY: skipAnchor
        ? undefined
        : animate
        ? (now.anchorY + anchorY) / 2
        : anchorY,
    });

    // Move to the intermediate value
    tooltip.getLabel().attr(now);

    // Run on next tick of the mouse tracker
    if (animate) {
      // Never allow two timeouts
      // @ts-ignore
      clearTimeout(this.tooltipTimeout);

      // Set the fixed interval ticking for the smooth tooltip
      // @ts-ignore
      this.tooltipTimeout = setTimeout(function () {
        // The interval function may still be running during destroy,
        // so check that the chart is really there before calling.
        if (tooltip) {
          tooltip.move(x, y, anchorX, anchorY);
        }
      }, 32);
    }
  }
);

export default LineChart;
